import { useAuth, useOrganizationList } from '@clerk/react';
import { useEffect, useRef } from 'react';

/**
 * If a signed-in user belongs to exactly one organization and that org isn't
 * yet active on the session, set it active.
 *
 * **Why this exists:** Clerk's JWT templates resolve `{{org.permissions}}` (and
 * the other `{{org.*}}` shortcodes) against the *active* organization on the
 * session. If a single-org user signs in without an active org set, the
 * minted JWT carries no permissions and every authed indexer call fails.
 *
 * **Why it lives in a hook:** the naive implementation (call setActive in a
 * useEffect that depends on hook outputs) loops infinitely; setActive
 * triggers a session update, which re-runs every Clerk hook, which re-runs
 * the effect, which calls setActive again, freezing the tab. This hook
 * encodes the two guards required to break that loop:
 *   1. A ref flag so we only attempt setActive *once* per mount.
 *   2. An `orgId === onlyOrg.id` early-return for sessions that already have
 *      the org active.
 *
 * **Where to mount:** call this once, at the app shell level (currently in
 * `<AutoActivateSingleOrg>` from `src/main.tsx`). Mounting it again in a page
 * is harmless but wasteful; the ref guard makes second invocations no-ops.
 *
 * For users with zero or multiple memberships, this is a no-op. Multi-org
 * users are expected to pick from a Clerk-hosted org switcher.
 */
export function useEnsureActiveOrg(): void {
  const { isSignedIn, orgId } = useAuth();
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const attempted = useRef(false);

  useEffect(() => {
    if (!isSignedIn) return;
    if (attempted.current) return;
    if (!isLoaded || !setActive) return;

    const memberships = userMemberships.data ?? [];
    if (memberships.length !== 1) return;
    const onlyOrg = memberships[0]?.organization;
    if (!onlyOrg) return;

    // Already active: record the attempt so we don't poll the hook on every
    // re-render, but skip the setActive call (calling it with the same org
    // would still cycle Clerk's session machinery).
    if (onlyOrg.id === orgId) {
      attempted.current = true;
      return;
    }

    attempted.current = true;
    void setActive({ organization: onlyOrg.id });
  }, [isSignedIn, isLoaded, setActive, userMemberships.data, orgId]);
}
